import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "../ui/Button"
import { DialogProps, Dialog } from "../ui/Dialog"
import clsx from "clsx"
import { PhotoRecord } from "@/lib/catalog"
import { applyAdjustmentDeltas, applyStyle } from "@/store/processing/image-processing"
import { AI } from "@/lib/ai/ai"
import { Spinner } from "../ui/Spinner"
import { Adjustments } from "@/core/types"
import { Icon } from "../icons/Icon"
import {
	isRemoteStyle,
	PreviewableStyle,
	tuneStyleWithPreviewAdjustments,
} from "@/store/style/style-state"
import { fetchAndRegisterStyleLuts } from "@/lib/catalog/helpers"
import { TuningAdjustmentSlider } from "../ui/TuningAdjustmentSlider"
import { getFileForPhoto } from "@/store/files-state"
import { getPhotoMetadata } from "@/store/photo/photos-state"
import { getDefaultAdjustments } from "@/core/adjustments"
import { useDebounce, useDebouncedValue } from "@/utils/useDebounce"
import { actionShortcuts } from "@/shortcuts/shortcuts"
import { useShortcuts } from "@/utils/useShortcuts"

type StylePreviewDialogProps<TStyle extends PreviewableStyle> = DialogProps & {
	style: TStyle
	samplePhotos: PhotoRecord[]
	onStyleFinalized: (style: TStyle) => void
	actionTitle?: string
}

export const StylePreviewDialog = <TStyle extends PreviewableStyle>(
	props: StylePreviewDialogProps<TStyle>,
) => {
	const { t } = useTranslation("components")
	const samplePhotos = props.samplePhotos
	const photoIds = samplePhotos.map((p) => p.id)
	const globalBiases = props.style.globalBiases ?? {}
	const [deltas, setDeltas] = useState({
		exposure: globalBiases.exposure ?? 0,
		contrast: globalBiases.contrast ?? 0,
		saturation: globalBiases.saturation ?? 0,
		temperature: globalBiases.temperature ?? 0,
		tint: globalBiases.tint ?? 0,
	})

	const [previews, setPreviews] = useState<Record<string, Blob>>({})
	const [loading, setLoading] = useState<Record<string, boolean>>({})
	const [adjustments, setAdjustments] = useState<Record<string, Adjustments>>({})
	const [viewMode, setViewMode] = useState<"single" | "grid">("grid")
	const [originalPreviews, setOriginalPreviews] = useState<Record<string, Blob>>({})
	const [isComparing, setIsComparing] = useState(false)
	const [initialLoadDone, setInitialLoadDone] = useState<Record<string, boolean>>({})

	// Debounce the transition from loading→not-loading to prevent button flashing
	const rawIsLoading = Object.values(loading).some(Boolean)
	const debouncedIsLoading = useDebouncedValue(rawIsLoading, 150)
	const isAnyLoading = rawIsLoading || debouncedIsLoading

	// Check if initial style application has completed for all photos (no matter success or failure)
	const isInitialLoadComplete = samplePhotos.every((photo) => initialLoadDone[photo.id])

	// Use ref to track latest deltas for debounced function
	const pendingDeltasRef = useRef(deltas)

	// Use ref to abort controller for cancellation
	const abortControllerRef = useRef<AbortController | null>(null)

	// Debounced preview generation - only runs after user stops adjusting
	const generatePreviews = useDebounce(async () => {
		// Cancel any ongoing preview generation
		if (abortControllerRef.current) {
			abortControllerRef.current.abort()
		}

		// Create new abort controller for this generation
		const abortController = new AbortController()
		abortControllerRef.current = abortController

		const currentDeltas = pendingDeltasRef.current
		for (const photo of samplePhotos) {
			// Check if we've been aborted before processing each photo
			if (abortController.signal.aborted) {
				return
			}

			const currentAdjustments = adjustments[photo.id]
			if (!currentAdjustments) continue

			try {
				setLoading((prev) => ({ ...prev, [photo.id]: true }))
				await AI.lockEditor()

				// Check again after acquiring lock
				if (abortController.signal.aborted) {
					return
				}

				const result = await applyAdjustmentDeltas(photo, currentAdjustments, currentDeltas)
				const preview = result?.preview
				const id = result?.id

				if (!abortController.signal.aborted && id && preview && preview instanceof Blob) {
					setPreviews((prev) => ({ ...prev, [id]: preview }))
				}
			} finally {
				void AI.unlockEditor()
				setLoading((prev) => ({ ...prev, [photo.id]: false }))
			}
		}

		// Clear the controller reference if this one completed
		if (abortControllerRef.current === abortController) {
			abortControllerRef.current = null
		}
	}, 150)

	const applyInitialStyle = async (photo: PhotoRecord) => {
		try {
			setLoading((prev) => ({ ...prev, [photo.id]: true }))

			await AI.lockEditor()

			// load original preview
			const file = getFileForPhoto(photo)
			if (!file) {
				throw new Error("Photo file not found")
			}

			const metadata = getPhotoMetadata(photo.id)
			await AI.loadImage(file, { metadata })

			const defaultAdjustments = getDefaultAdjustments(photo)
			const originalPreview = await AI.generatePreview({ adjustments: defaultAdjustments })

			if (originalPreview instanceof Blob) {
				setOriginalPreviews((prev) => ({ ...prev, [photo.id]: originalPreview }))
			}

			const result = await applyStyle({
				photo,
				style: props.style,
				ignoreClustering: true,
				forceApply: true,
			})
			const preview = await result?.preview
			const id = result?.imageInfo?.imageId
			const currentDeltas = pendingDeltasRef.current
			const hasBias = Object.values(currentDeltas).some((value) => value !== 0)

			if (id && result?.adjustments) {
				setAdjustments((prev) => ({ ...prev, [id]: result.adjustments }))
			}

			if (id && preview && preview instanceof Blob && !hasBias) {
				setPreviews((prev) => ({ ...prev, [id]: preview }))
			}

			if (id && result?.adjustments && hasBias) {
				const biasedResult = await applyAdjustmentDeltas(
					photo,
					result.adjustments,
					currentDeltas,
				)
				const biasedPreview = biasedResult?.preview
				if (biasedPreview && biasedPreview instanceof Blob) {
					setPreviews((prev) => ({ ...prev, [id]: biasedPreview }))
				}
			}
		} finally {
			void AI.unlockEditor()
			setLoading((prev) => ({ ...prev, [photo.id]: false }))
			setInitialLoadDone((prev) => ({ ...prev, [photo.id]: true }))
		}
	}

	useShortcuts({
		keys: [actionShortcuts.showBefore],
		action: (event) => {
			if (event.type === "keydown") {
				setIsComparing(true)
			} else {
				setIsComparing(false)
			}
		},
		trackingLabel: "style_view_before_after",
		scope: "dialog",
		disabled: !isInitialLoadComplete,
		keyup: true,
		keydown: true,
	})

	useEffect(() => {
		const run = async () => {
			if (isRemoteStyle(props.style)) {
				await fetchAndRegisterStyleLuts(props.style.response.lutUrls)
			}
			for (const photo of samplePhotos) {
				void applyInitialStyle(photo)
			}
		}
		void run()

		// Cleanup: abort any pending preview generation when unmounting
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort()
			}
		}
	}, [])

	const toggleCompare = (event: React.PointerEvent) => {
		if (event.type === "pointerdown") {
			setIsComparing(true)
			event.currentTarget.setPointerCapture(event.pointerId)
		} else if (event.type === "pointerup") {
			setIsComparing(false)
			event.currentTarget.releasePointerCapture(event.pointerId)
		}
	}

	const finalizeStyle = () => {
		const updatedStyle = tuneStyleWithPreviewAdjustments(props.style, photoIds, deltas)
		props.onStyleFinalized(updatedStyle)
	}

	const updateAdjustments = (key: string, val: number) => {
		const updated = { ...deltas, [key]: val }
		setDeltas(updated)
		pendingDeltasRef.current = updated
		generatePreviews()
	}

	const renderTuningAdjustments = (
		name: string,
		key: keyof typeof deltas,
		leftLabel: string,
		rightLabel: string,
	) => {
		const currentValue = deltas[key]

		const handleChange = (val: number) => {
			void updateAdjustments(key, val)
		}

		return (
			<div
				role="group"
				aria-label={t("stylePreviewDialog.ariaTuneAdjustment", { name })}
				className="flex size-full flex-col items-start justify-center space-y-2"
			>
				<div aria-hidden className="text-sm">
					{name}
				</div>
				<TuningAdjustmentSlider
					name={name}
					value={currentValue}
					onChange={handleChange}
					leftLabel={leftLabel}
					rightLabel={rightLabel}
					disabled={!isInitialLoadComplete}
				/>
			</div>
		)
	}

	return (
		<Dialog
			contentSize="lg"
			{...props}
			canGoBack={props.canGoBack ?? true}
			bottomButtons={
				<div className="flex w-full flex-1 flex-row items-center justify-end space-x-2">
					<Button
						trackingLabel="finalize_style"
						aria-label={t("stylePreviewDialog.ariaFinalizeAiStyleCreation")}
						variant="primary"
						onClick={finalizeStyle}
						disabled={isAnyLoading}
					>
						{props.actionTitle ?? t("stylePreviewDialog.finalizeTitle")}
					</Button>
				</div>
			}
		>
			<div className="flex size-full flex-col overflow-hidden">
				<div className="text-md font-bold">{t("stylePreviewDialog.previewTune")}</div>
				<div className="pt-2 text-start text-sm text-base-400">
					{t("stylePreviewDialog.previewDescription")}
				</div>

				<div className="flex size-full flex-row items-center justify-center space-x-4 overflow-hidden pt-5">
					<div className="flex h-full w-1/2 flex-col items-center space-y-2 rounded-md bg-base-900 p-2">
						<div className="flex w-full flex-1 flex-row items-center justify-between self-center">
							<div className="flex flex-row items-center">
								<Button
									trackingLabel="grid"
									aria-label={t("stylePreviewDialog.ariaShowPreviewPhotosAsGrid")}
									variant="icon"
									size="custom"
									onClick={() => setViewMode("grid")}
									className={clsx(
										"mx-0 rounded-sm p-1",
										viewMode === "grid"
											? "bg-accent-600 hover:bg-accent-600"
											: "bg-base-700 hover:bg-base-700",
									)}
								>
									<Icon className="flex flex-none" name="grid_view" />
								</Button>
								<Button
									aria-label={t("stylePreviewDialog.ariaShowSinglePreviewPhoto")}
									trackingLabel="single"
									variant="icon"
									size="custom"
									onClick={() => setViewMode("single")}
									className={clsx(
										"mx-2 rounded-sm p-1",
										viewMode === "single"
											? "bg-accent-600 hover:bg-accent-600"
											: "bg-base-700 hover:bg-base-700",
									)}
								>
									<Icon className="flex flex-none" name="focus" />
								</Button>
							</div>
							<Button
								trackingLabel="style_view_before_after"
								aria-label={t("stylePreviewDialog.ariaTogglePreviewBeforeAfter")}
								variant="icon"
								size="custom"
								tooltip={t("stylePreviewDialog.tooltipBeforeAfter")}
								className="mx-0 p-1"
								onPointerDown={toggleCompare}
								onPointerUp={toggleCompare}
							>
								<Icon name="compare" size={17} />
							</Button>
						</div>
						{viewMode === "single" ? (
							<StyleTuningSinglePreview
								previews={previews}
								originalPreviews={originalPreviews}
								isLoading={loading}
								photoIds={photoIds}
								isComparing={isComparing}
							/>
						) : (
							<StyleTuningGridPreview
								previews={previews}
								originalPreviews={originalPreviews}
								isLoading={loading}
								photoIds={photoIds}
								isComparing={isComparing}
							/>
						)}
					</div>
					<div className="flex h-full min-h-0 w-1/2 flex-col items-start justify-start space-y-2 overflow-y-auto overflow-x-hidden rounded-md bg-base-700 p-4">
						{renderTuningAdjustments(
							t("stylePreviewDialog.labelExposure"),
							"exposure",
							t("stylePreviewDialog.labelDarker"),
							t("stylePreviewDialog.labelBrighter"),
						)}
						{renderTuningAdjustments(
							t("stylePreviewDialog.labelContrast"),
							"contrast",
							t("stylePreviewDialog.labelSofter"),
							t("stylePreviewDialog.labelStronger"),
						)}
						{renderTuningAdjustments(
							t("stylePreviewDialog.labelSaturation"),
							"saturation",
							t("stylePreviewDialog.labelMuted"),
							t("stylePreviewDialog.labelVibrant"),
						)}
						{renderTuningAdjustments(
							t("stylePreviewDialog.labelTemperature"),
							"temperature",
							t("stylePreviewDialog.labelCooler"),
							t("stylePreviewDialog.labelWarmer"),
						)}
						{renderTuningAdjustments(
							t("stylePreviewDialog.labelTint"),
							"tint",
							t("stylePreviewDialog.labelGreen"),
							t("stylePreviewDialog.labelMagenta"),
						)}
					</div>
				</div>
			</div>
		</Dialog>
	)
}

const useTuningPreviewURL = ({ previewData }: { previewData?: Blob }) => {
	const [url, setUrl] = useState<string | undefined>(undefined)

	useEffect(() => {
		if (previewData) {
			const newUrl = URL.createObjectURL(previewData)
			setUrl(newUrl)
		} else {
			setUrl(undefined)
		}

		return () => {
			if (url) {
				URL.revokeObjectURL(url)
				setUrl(undefined)
			}
		}
	}, [previewData])

	return url
}

const StyleTuningPreview = (props: {
	previewData?: Blob
	originalData?: Blob
	isLoading?: boolean
	className?: string
	isSelected?: boolean
	isComparing?: boolean
}) => {
	const previewUrl = useTuningPreviewURL({ previewData: props.previewData })
	const originalUrl = useTuningPreviewURL({ previewData: props.originalData })

	const displayUrl = props.isComparing ? originalUrl : previewUrl

	return (
		<div
			className={clsx(
				"relative flex size-full shrink-0 items-center justify-center overflow-hidden bg-base-900",
				"outline outline-2 outline-offset-[-2px]",
				props.isSelected ? "outline-accent-600" : "outline-transparent",
				props.className,
			)}
		>
			{displayUrl && <img className={clsx("size-full", props.className)} src={displayUrl} />}
			{props.isLoading && <Spinner className="absolute" size={20} />}
		</div>
	)
}

const StyleTuningSinglePreview = (props: {
	photoIds: string[]
	previews: Record<string, Blob>
	originalPreviews: Record<string, Blob>
	isLoading: Record<string, boolean>
	isComparing?: boolean
}) => {
	const photoIds = props.photoIds
	const [currentPhotoId, setCurrentPhotoId] = useState<string | undefined>(photoIds[0])

	return (
		<div className="flex size-full min-h-0 flex-col overflow-hidden">
			{/* Large preview fills remaining vertical space */}
			<div className="flex min-h-0 w-full flex-1 overflow-hidden">
				{currentPhotoId && (
					<StyleTuningPreview
						previewData={props.previews[currentPhotoId]}
						originalData={props.originalPreviews[currentPhotoId]}
						isLoading={props.isLoading[currentPhotoId]}
						className="size-full object-contain"
						isComparing={props.isComparing}
					/>
				)}
			</div>

			{/* Fixed-height thumbnail strip */}
			<div className="mt-2 flex h-12 shrink-0 flex-row justify-center gap-2">
				{photoIds.map((id) => (
					<div
						key={id}
						className={clsx("aspect-square size-12 overflow-hidden")}
						onClick={() => setCurrentPhotoId(id)}
					>
						<StyleTuningPreview
							previewData={props.previews[id]}
							originalData={props.originalPreviews[id]}
							isLoading={props.isLoading[id]}
							className="aspect-square size-full object-cover"
							isSelected={currentPhotoId === id}
							isComparing={props.isComparing}
						/>
					</div>
				))}
			</div>
		</div>
	)
}

const StyleTuningGridPreview = (props: {
	photoIds: string[]
	previews: Record<string, Blob>
	originalPreviews: Record<string, Blob>
	isLoading: Record<string, boolean>
	isComparing?: boolean
}) => {
	const photoIds = props.photoIds

	return (
		<div className="grid size-full grid-cols-2 grid-rows-2 gap-2 overflow-hidden bg-base-900">
			{Array.from({ length: 4 }).map((_, index) => {
				const id = photoIds[index]
				return (
					<StyleTuningPreview
						key={id}
						previewData={id ? props.previews[id] : undefined}
						originalData={id ? props.originalPreviews[id] : undefined}
						isLoading={id ? props.isLoading[id] : false}
						className="object-cover"
						isComparing={props.isComparing}
					/>
				)
			})}
		</div>
	)
}
